import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import Select from "react-select";
import ConfirmModal from "../../../layout/modal";

import {
    manageable as manageableCalendars,
    users as calendarUsers,
    search as searchCalendarUsers,
    share as shareCalendarUser,
    update as updateCalendarUser,
    unshare as unshareCalendarUser,
} from "../../../actions/calendar.users";

const CALENDAR_USER_ROLES = [
    {
        value: "viewer",
        label: "Viewer"
    },
    {
        value: "editor",
        label: "Editor"
    },
    {
        value: "admin",
        label: "Administrator"
    }
];

class Shares extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            loadingUsers: false,
            updatingUserUuid: null,
            removingUserUuid: null,
            adding: false,

            calendars: [],
            users: [],
            selectedCalendarUuid: "",

            searchText: "",
            searchResults: [],
            searching: false,
            selectedStaffUuid: "",
            selectedStaffName: "",
            selectedRole: "viewer",

            error: null,
            usersError: null,
            searchError: null,
            addError: null,
            confirm: {
                show: false,
                userUuid: null,
                name: ""
            }
        };
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.show && this.props.show) {
            this.loadCalendars();
        }
    }

    resetState = () => {
        this.setState({
            loading: false,
            loadingUsers: false,
            updatingUserUuid: null,
            removingUserUuid: null,
            adding: false,

            calendars: [],
            users: [],
            selectedCalendarUuid: "",

            searchText: "",
            searchResults: [],
            searching: false,
            selectedStaffUuid: "",
            selectedStaffName: "",
            selectedRole: "viewer",

            error: null,
            usersError: null,
            searchError: null,
            addError: null,
            confirm: {
                show: false,
                userUuid: null,
                name: ""
            }

        });
    };

    loadCalendars = async () => {
        this.setState({
            loading: true,
            error: null,
            usersError: null,
            calendars: [],
            users: [],
            selectedCalendarUuid: ""
        });

        try {
            const response = await this.props.manageableCalendars();
            const calendars = response?.items || [];
            const selectedCalendarUuid = calendars[0]?.uuid || "";

            this.setState({
                calendars,
                selectedCalendarUuid,
                loading: false
            });

            if (selectedCalendarUuid) {
                await this.loadUsers(selectedCalendarUuid);
            }
        } catch (error) {
            this.setState({
                loading: false,
                error: "Unable to load calendars."
            });
        }
    };

    loadUsers = async (calendarUuid = this.state.selectedCalendarUuid) => {
        if (!calendarUuid) {
            this.setState({
                users: [],
                usersError: null,
                searchError: null,
                addError: null
            });
            return;
        }

        this.setState({
            loadingUsers: true,
            usersError: null,
            searchError: null,
            addError: null
        });

        try {
            const response = await this.props.calendarUsers(calendarUuid);

            this.setState({
                users: response?.items || [],
                loadingUsers: false
            });
        } catch (error) {
            this.setState({
                users: [],
                loadingUsers: false,
                usersError: "Unable to load shared users."
            });
        }
    };

    onCalendarChange = async (option) => {
        const selectedCalendarUuid = option?.value || "";

        this.setState({
            selectedCalendarUuid,
            users: [],
            usersError: null,

            searchText: "",
            searchResults: [],
            selectedStaffUuid: "",
            selectedStaffName: "",
            selectedRole: "viewer",
            searchError: null
        });

        await this.loadUsers(selectedCalendarUuid);
    };

    onRoleChange = async (userUuid, role) => {
        const { selectedCalendarUuid, users } = this.state;

        if (!selectedCalendarUuid || !userUuid || !role) {
            return;
        }

        const current = users.find((item) => item.uuid === userUuid);

        if (current?.role === role) {
            return;
        }

        this.setState({
            updatingUserUuid: userUuid,
            usersError: null
        });

        try {
            await this.props.updateCalendarUser(
                selectedCalendarUuid,
                userUuid,
                { role }
            );

            await this.loadUsers(selectedCalendarUuid);

            this.setState({
                updatingUserUuid: null
            });
        } catch (error) {
            this.setState({
                updatingUserUuid: null,
                usersError: "Unable to update calendar role."
            });
        }
    };
    onUnshareClick = (userUuid) => {
        const user = this.state.users.find((item) => item.uuid === userUuid);
        const member = user?.member || user?.Member || {};
        const name = this.displayName(member) || "this user";

        this.setState({
            confirm: {
                show: true,
                userUuid,
                name
            }
        });
    };

    onCancelUnshare = () => {
        this.setState({
            confirm: {
                show: false,
                userUuid: null,
                name: ""
            }
        });
    };

    onConfirmUnshare = async () => {
        const { selectedCalendarUuid, confirm } = this.state;
        const userUuid = confirm.userUuid;

        if (!selectedCalendarUuid || !userUuid) {
            this.onCancelUnshare();
            return;
        }

        this.setState({
            removingUserUuid: userUuid,
            usersError: null,
            confirm: {
                show: false,
                userUuid: null,
                name: ""
            }
        });

        try {
            await this.props.unshareCalendarUser(
                selectedCalendarUuid,
                userUuid
            );

            await this.loadUsers(selectedCalendarUuid);

            this.setState({
                removingUserUuid: null,
                searchText: "",
                searchResults: [],
                selectedStaffUuid: "",
                selectedStaffName: "",
                selectedStaffEmail: "",
                selectedRole: "viewer"
            });
        } catch (error) {
            this.setState({
                removingUserUuid: null,
                usersError: "Unable to remove calendar access."
            });
        }
    };

    onSearchTextChange = async (event) => {
        const searchText = event.target.value;

        this.setState({
            searchText,
            selectedStaffUuid: "",
            selectedStaffName: "",
            searchError: null
        });

        if (!this.state.selectedCalendarUuid) {
            return;
        }

        if (searchText.trim().length < 2) {
            this.setState({
                searchResults: []
            });
            return;
        }

        this.setState({
            searching: true
        });

        try {
            const response = await this.props.searchCalendarUsers(
                this.state.selectedCalendarUuid,
                searchText
            );

            this.setState({
                searching: false,
                searchResults: response?.items || []
            });
        } catch (error) {
            this.setState({
                searching: false,
                searchResults: [],
                searchError: "Unable to search Staff."
            });
        }
    };

    onStaffSelect = (staff) => {
        this.setState({
            selectedStaffUuid: staff.staffUuid,
            selectedStaffName:
                staff.name ||
                [staff.firstName, staff.lastName].filter(Boolean).join(" ") ||
                staff.email ||
                "Selected Staff",
            searchText:
                staff.name ||
                [staff.firstName, staff.lastName].filter(Boolean).join(" ") ||
                staff.email ||
                "",
            searchResults: []
        });
    };

    onSelectedRoleChange = (option) => {
        this.setState({
            selectedRole: option?.value || "viewer"
        });
    };

    get calendarOptions() {
        return (this.state.calendars || []).map((calendar) => ({
            value: calendar.uuid,
            label: calendar.caption || calendar.name || calendar.uuid,
        }));
    }

    get selectedCalendarOption() {
        return this.calendarOptions.find((option) => option.value === this.state.selectedCalendarUuid) || null;
    }

    get selectedRoleOption() {
        return CALENDAR_USER_ROLES.find((role) => role.value === this.state.selectedRole) || CALENDAR_USER_ROLES[0];
    }

    onAddShareClick = async () => {
        const {
            selectedCalendarUuid,
            selectedStaffUuid,
            selectedRole
        } = this.state;

        if (!selectedCalendarUuid || !selectedStaffUuid || !selectedRole) {
            return;
        }

        this.setState({
            adding: true,
            addError: null,
            searchError: null
        });

        try {
            await this.props.shareCalendarUser(
                selectedCalendarUuid,
                {
                    staffUuid: selectedStaffUuid,
                    role: selectedRole
                }
            );

            await this.loadUsers(selectedCalendarUuid);

            this.setState({
                adding: false,
                searchText: "",
                searchResults: [],
                selectedStaffUuid: "",
                selectedStaffName: "",
                selectedRole: "viewer"
            });
        } catch (error) {
            this.setState({
                adding: false,
                addError: "Unable to share calendar with selected Staff."
            });
        }
    };

    onCancel = () => {
        this.resetState();

        if (this.props.onHide) {
            this.props.onHide();
        }
    };

    renderAddRow = () => {
        return (
            <tr className="table-light">
                <td className="position-relative" colSpan={2}>
                    <Form.Control
                        type="text"
                        size="sm"
                        value={this.state.searchText}
                        onChange={this.onSearchTextChange}
                        placeholder="Search Staff to add"
                        autoComplete="off"
                        disabled={
                            !this.state.selectedCalendarUuid ||
                            this.state.loadingUsers ||
                            this.state.adding
                        }
                    />

                    {this.state.searching ? (
                        <div className="small text-muted mt-1">
                            Searching...
                        </div>
                    ) : null}

                    {this.state.searchResults.length > 0 ? (
                        <div
                            className="border rounded bg-white position-absolute w-100 shadow-sm"
                            style={{
                                zIndex: 1056,
                                maxHeight: "180px",
                                overflowY: "auto"
                            }}
                        >
                            {this.state.searchResults.map((staff) => {
                                const label =
                                    staff.name ||
                                    [staff.firstName, staff.lastName].filter(Boolean).join(" ") ||
                                    staff.email ||
                                    staff.staffUuid;

                                return (
                                    <button
                                        key={staff.staffUuid}
                                        type="button"
                                        className="btn btn-link text-start text-decoration-none w-100 px-3 py-2"
                                        onClick={() => this.onStaffSelect(staff)}
                                    >
                                        <div>{label}</div>
                                        {staff.email ? (
                                            <div className="small text-muted">
                                                {staff.email}
                                            </div>
                                        ) : null}
                                    </button>
                                );
                            })}
                        </div>
                    ) : null}
                </td>

                <td>
                    <Select
                        inputId="selectedCalendarShareRole"
                        classNamePrefix="react-select"
                        value={this.selectedRoleOption}
                        onChange={this.onSelectedRoleChange}
                        isDisabled={this.state.adding}
                        isClearable={false}
                        options={CALENDAR_USER_ROLES}
                    />
                </td>

                <td className="text-end">
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={
                            !this.state.selectedStaffUuid ||
                            this.state.adding ||
                            this.state.loadingUsers
                        }
                        onClick={this.onAddShareClick}
                    >
                        {this.state.adding ? "Adding..." : "Add"}
                    </Button>
                </td>
            </tr>
        );
    };

    renderUnshareConfirm = () => {
        const { confirm, removingUserUuid } = this.state;

        return (
            <ConfirmModal
                show={confirm.show}
                title="Remove Calendar Access"
                body={
                    <>
                        Are you sure you want to remove{" "}
                        <strong>{confirm.name || "this user"}</strong>
                        {" "}from this calendar?
                    </>
                }
                buttons={[
                    {
                        caption: "Cancel",
                        variant: "secondary",
                        default: true,
                        enabled: !removingUserUuid,
                        onClick: this.onCancelUnshare
                    },
                    {
                        caption: removingUserUuid ? "Removing..." : "Remove",
                        variant: "danger",
                        enabled: !removingUserUuid,
                        onClick: this.onConfirmUnshare
                    }
                ]}
                onCancel={this.onCancelUnshare}
            />
        );
    };

    render() {
        const { show } = this.props;

        return (
            <>
                <Modal
                    show={show}
                    onHide={this.onCancel}
                    size="lg"
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Share Calendars</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {this.state.error ? (
                            <Alert variant="danger">
                                {this.state.error}
                            </Alert>
                        ) : null}

                        {this.state.loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                            </div>
                        ) : null}

                        {!this.state.loading && this.state.calendars.length === 0 ? (
                            <div className="text-muted">
                                There are no calendars available for sharing.
                            </div>
                        ) : null}

                        {!this.state.loading && this.state.calendars.length > 0 ? (
                            <Form.Group className="mb-3">
                                <Form.Label>Calendar</Form.Label>
                                <Select
                                    inputId="selectedCalendarUuid"
                                    classNamePrefix="react-select"
                                    value={this.selectedCalendarOption}
                                    onChange={this.onCalendarChange}
                                    isClearable={false}
                                    options={this.calendarOptions}
                                />
                            </Form.Group>
                        ) : null}

                        {this.state.usersError ? (
                            <Alert variant="danger">
                                {this.state.usersError}
                            </Alert>
                        ) : null}

                        {this.state.selectedCalendarUuid ? (
                            <div className="mt-4">
                                <h6 className="mb-3">Shared With</h6>

                                {this.state.loadingUsers ? (
                                    <div className="text-center py-3">
                                        <Spinner animation="border" size="sm" />
                                    </div>
                                ) : null}

                                {!this.state.loadingUsers ? (
                                    <div className="table-responsive">
                                        <table className="table table-sm align-middle">
                                            <thead>
                                                <tr>
                                                    <th>User</th>
                                                    <th>Email</th>
                                                    <th style={{ width: "180px" }}>Role</th>
                                                    <th className="text-end" style={{ width: "100px" }}>
                                                        Action
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {this.state.users.map((item) => {
                                                    const member = item.member || item.Member || {};
                                                    const displayName = [member.lastName, member.firstName].filter(Boolean).join(", ") || member.displayName || member.email || "Unknown User";

                                                    return (
                                                        <tr key={item.uuid}>
                                                            <td>{displayName}</td>
                                                            <td>{member.email || ""}</td>
                                                            <td>
                                                                <Select
                                                                    inputId={`calendarRole-${item.uuid}`}
                                                                    classNamePrefix="react-select"
                                                                    value={CALENDAR_USER_ROLES.find((role) => role.value === (item.role || "viewer")) || CALENDAR_USER_ROLES[0]}
                                                                    isDisabled={this.state.updatingUserUuid === item.uuid}
                                                                    onChange={(option) => this.onRoleChange(item.uuid, option?.value || "viewer")}
                                                                    isClearable={false}
                                                                    options={CALENDAR_USER_ROLES}
                                                                />

                                                                {this.state.updatingUserUuid === item.uuid ? (
                                                                    <div className="small text-muted mt-1">
                                                                        Saving...
                                                                    </div>
                                                                ) : null}
                                                            </td>
                                                            <td className="text-end">
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    disabled={
                                                                        this.state.removingUserUuid === item.uuid ||
                                                                        this.state.updatingUserUuid === item.uuid
                                                                    }
                                                                    onClick={() => this.onUnshareClick(item.uuid)}
                                                                >
                                                                    {this.state.removingUserUuid === item.uuid ? "Removing..." : "Remove"}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}

                                                {this.state.users.length === 0 ? (
                                                    <tr>
                                                        <td className="text-muted" colSpan={4}>
                                                            No staff currently has access to this calendar.
                                                        </td>
                                                    </tr>
                                                ) : null}

                                                {this.renderAddRow()}
                                            </tbody>
                                        </table>

                                    </div>
                                ) : null}

                            </div>
                        ) : null}
                    </Modal.Body>

                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={this.onCancel}
                        >
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
                {this.renderUnshareConfirm()}
            </>
        );
    }
}

Shares.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func.isRequired,
    manageableCalendars: PropTypes.func.isRequired,
    calendarUsers: PropTypes.func.isRequired,
    searchCalendarUsers: PropTypes.func.isRequired,
    shareCalendarUser: PropTypes.func.isRequired,
    updateCalendarUser: PropTypes.func.isRequired,
    unshareCalendarUser: PropTypes.func.isRequired,
};

Shares.defaultProps = {
    show: false,
};

export default connect(null, {
    manageableCalendars,
    calendarUsers,
    searchCalendarUsers,
    shareCalendarUser,
    updateCalendarUser,
    unshareCalendarUser
})(Shares);
